import React from 'react';

import {VizConfigItem} from './config';

type VisibilityMap = Record<string, boolean>;
type VisibilityState = Record<string, VisibilityMap>;

type ContextValue = {
  visibilityByVizKey: VisibilityState;
  setVisibilityForViz: (
    timeSeriesVizKey: string,
    updater: (prev: VisibilityMap | undefined) => VisibilityMap
  ) => void;
};

const VisibilityContext = React.createContext<ContextValue | undefined>(undefined);

export function VizVisibilityProvider({children}: {children: React.ReactNode}) {
  const [visibilityByVizKey, setVisibilityByVizKey] = React.useState<VisibilityState>({});

  const setVisibilityForViz = React.useCallback(
    (timeSeriesVizKey: string, updater: (prev: VisibilityMap | undefined) => VisibilityMap) => {
      setVisibilityByVizKey((prev) => {
        const nextForKey = updater(prev[timeSeriesVizKey]);
        if (prev[timeSeriesVizKey] === nextForKey) {
          return prev;
        }
        return {...prev, [timeSeriesVizKey]: nextForKey};
      });
    },
    []
  );

  const value = React.useMemo<ContextValue>(
    () => ({visibilityByVizKey, setVisibilityForViz}),
    [visibilityByVizKey, setVisibilityForViz]
  );

  return <VisibilityContext.Provider value={value}>{children}</VisibilityContext.Provider>;
}

/**
 * Hook to access and maintain visibility for a given TimeSeriesViz
 * Use the title + groupId as the timeSeriesVizKey as it only needs to be unique within the parent Viz component.
 * Preserves visibility for the lifecycle of the Viz component. This includes loading new data, forecast times,
 * and switching sites within a project.
 */
export function useVizVisibility(
  timeSeriesVizKey: string,
  items: VizConfigItem[]
): {
  isVisibleById: VisibilityMap;
  onLegendChange: (ids: string[], nextValue: boolean) => void;
  resetVisibility: () => void;
} {
  const context = React.useContext(VisibilityContext);
  if (context === undefined) {
    throw new Error('useVizVisibility must be used within a VizVisibilityProvider');
  }
  const {setVisibilityForViz, visibilityByVizKey} = context;

  // Items that drop out keep their entry, so a choice survives a refetch that briefly
  // removes its item; only items never seen before get their default.
  React.useEffect(() => {
    setVisibilityForViz(timeSeriesVizKey, (prev) => {
      const prevMap = prev ?? {};
      const newItems = items.filter((c) => !(c.id in prevMap));
      if (newItems.length === 0) return prevMap;
      const next: VisibilityMap = {...prevMap};
      for (const c of newItems) {
        next[c.id] = !c.defaultNotVisible;
      }
      return next;
    });
  }, [timeSeriesVizKey, items, setVisibilityForViz]);

  const onLegendChange = React.useCallback(
    (ids: string[], nextValue: boolean) => {
      setVisibilityForViz(timeSeriesVizKey, (prev) => {
        const next = {...(prev || {})};
        ids.forEach((id) => {
          next[id] = nextValue;
        });
        return next;
      });
    },
    [timeSeriesVizKey, setVisibilityForViz]
  );

  // Fallback to all-visible until effect initializes
  const fallback = React.useMemo(
    () => items.reduce<VisibilityMap>((acc, c) => ({...acc, [c.id]: !c.defaultNotVisible}), {}),
    [items]
  );

  const resetVisibility = React.useCallback(() => {
    setVisibilityForViz(timeSeriesVizKey, () => fallback);
  }, [setVisibilityForViz, timeSeriesVizKey, fallback]);

  return {
    isVisibleById: visibilityByVizKey[timeSeriesVizKey] || fallback,
    onLegendChange,
    resetVisibility,
  };
}
